// Strategic sites: capture weights, contest, decay, neutralize, ownership flips, ascension (§12).

import { factionDef } from '../../content/factions';
import { buildingDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import type { PlayerId } from '../../content/types';
import { dist } from '../../shared/vec';
import type { Sim } from '../sim';

const dt = TUNING.dt;

function captureRate(weight: number): number {
  // §12.2 lookup: 1 / 2 / 3+ effective weights ≈ 18 / 12 / 8 s
  if (weight <= 0) return 0;
  if (weight < 2) return TUNING.captureRateByWeight[1] * weight;
  if (weight < 3) return TUNING.captureRateByWeight[2] * (weight / 2);
  return TUNING.captureRateByWeight[3];
}

export function stepSites(sim: Sim): void {
  const st = sim.state;
  for (const site of st.sites) {
    // owned sites trickle resources on their own — holding ground pays even with no workers there
    if (site.owner !== -1) {
      const p = st.players[site.owner];
      p.power += TUNING.siteTricklePowerPerSec * dt;
      p.essence += TUNING.siteTrickleEssencePerSec * dt;
    }
    // Gather per-player capture weight while contesting by TEAM. Allied presence never
    // stalls a channel, and only the player issuing Capture receives ownership/rewards.
    const weights = new Map<PlayerId, number>();
    const presentTeams = new Set<number>();
    for (const s of st.squads.values()) {
      const d = dist(s.pos, site.pos);
      if (d > TUNING.captureRadius) continue;
      presentTeams.add(sim.teamOf(s.owner));
      if (s.command.kind === 'capture' && s.command.siteId === site.id) {
        if (s.isEcho) continue; // Astral Echoes cannot capture sites
        const cw = unitDef(s.defId).captureWeight;
        weights.set(s.owner, (weights.get(s.owner) ?? 0) + cw);
      }
    }
    site.contested = presentTeams.size > 1;

    if (site.contested) continue; // pause both ways (§12.2)

    const ranked = [...weights.entries()]
      .filter(([, weight]) => weight > 0)
      .sort((a, b) => b[1] - a[1] || a[0] - b[0]);
    const capturer = ranked[0]?.[0];
    const weight = Math.min(3, ranked[0]?.[1] ?? 0);

    if (capturer === undefined) {
      // decay abandoned progress after grace
      if (site.meter > 0) {
        site.decayGrace += dt;
        if (site.decayGrace >= TUNING.captureDecayGraceSeconds) {
          site.meter = Math.max(0, site.meter - TUNING.captureDecayPerSecond * dt);
          if (site.meter === 0) site.meterPlayer = -1;
        }
      }
      // neutralize progress regains toward owner control
      if (site.owner !== -1 && site.control < 100) {
        site.decayGrace += dt;
        if (site.decayGrace >= TUNING.captureDecayGraceSeconds) {
          site.control = Math.min(100, site.control + TUNING.captureDecayPerSecond * dt);
        }
      }
      continue;
    }
    site.decayGrace = 0;

    if (site.owner !== -1 && sim.areAllies(site.owner, capturer)) continue; // already allied territory

    const rate = captureRate(weight);
    if (site.owner !== -1) {
      // neutralize first: drain control 100 → 0
      site.control -= rate * dt;
      if (site.control <= 0) {
        const loser = site.owner as PlayerId;
        site.owner = -1;
        site.control = 0;
        site.meter = 0;
        site.meterPlayer = capturer;
        sim.removeSiteTower(site.id);
        sim.pushEvent({ type: 'SiteNeutralized', site: site.id });
        sim.pushEvent({ type: 'SiteLost', site: site.id, loser });
        sim.recomputeCaps(loser);
        sim.updateAscension(loser);
      }
    } else {
      if (site.meterPlayer !== capturer) {
        site.meter = 0;
        site.meterPlayer = capturer;
      }
      site.meter += rate * dt;
      if (site.meter >= 100) {
        site.meter = 100;
        site.owner = capturer;
        site.control = 100;
        // the site tower rises for the new owner (produces workers; upgradable defense)
        const towerId = factionDef(st.players[capturer].faction).siteTowerDefId;
        sim.spawnBuilding(towerId, capturer, site.pos, { complete: true, siteId: site.id });
        for (const building of st.buildings.values()) {
          if (building.owner !== capturer || building.state !== 'active') continue;
          const boost = buildingDef(building.defId).traits?.find((t) => t.kind === 'captureTraining');
          if (boost) building.captureTrainingRemaining = Math.max(building.captureTrainingRemaining, boost.duration);
        }
        // new zone opened — over-capacity workers stream over and balance out (owner request)
        sim.rebalanceWorkers(capturer);
        sim.pushEvent({ type: 'SiteCaptured', site: site.id, owner: capturer });
        st.players[capturer].stats.sitesCaptured++;
        // Capture → Defend auto-conversion (§12.3)
        for (const s of st.squads.values()) {
          if (
            s.owner === capturer &&
            s.command.kind === 'capture' &&
            s.command.siteId === site.id
          ) {
            s.command = { kind: 'defend', pointA: { ...site.pos }, siteId: site.id, auto: true };
          }
        }
        sim.recomputeCaps(capturer);
        sim.updateAscension(capturer);
      }
    }
  }
}
